// GoogleMapLoader.js
// Este componente se encarga de cargar la API de Google Maps de forma centralizada

// Variable global para rastrear el estado de carga
let googleMapsLoadPromise = null;

/**
 * Carga la API de Google Maps de forma centralizada
 * @param {string} apiKey - La clave de API de Google Maps
 * @param {Array} libraries - Las bibliotecas a cargar (places, geometry, etc.)
 * @returns {Promise} - Una promesa que se resuelve cuando la API está cargada
 */
export const loadGoogleMapsApi = (apiKey, libraries = ['places', 'geometry']) => {
  // Si ya hay una promesa de carga en curso, devolver esa
  if (googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }

  // Si la API ya está cargada, devolver una promesa resuelta
  if (window.google && window.google.maps && window.google.maps.Map) {
    //console.log('Google Maps API ya está cargada, usando la instancia existente');
    return Promise.resolve(window.google);
  }

  // Crear una nueva promesa para cargar la API
  googleMapsLoadPromise = new Promise((resolve, reject) => {
    // Función de callback para cuando la API se carga correctamente
    window.googleMapsCallback = () => {
      resolve(window.google);
    };

    // Crear el script para cargar la API
    const script = document.createElement('script');
    const librariesParam = libraries.join(',');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${librariesParam}&callback=googleMapsCallback`;
    script.async = true;
    script.defer = true;
    
    // Manejar errores de carga
    script.onerror = (error) => {
      console.error('Error al cargar Google Maps API:', error);
      googleMapsLoadPromise = null; // Resetear la promesa para permitir reintentos
      reject(error);
    };

    // Agregar el script al DOM
    document.head.appendChild(script);
  });

  return googleMapsLoadPromise;
};

/**
 * Verifica si la API de Google Maps está completamente cargada
 * @returns {boolean} - true si la API está cargada, false en caso contrario
 */
export const isGoogleMapsLoaded = () => {
  return !!(
    window.google &&
    window.google.maps &&
    window.google.maps.Map &&
    typeof window.google.maps.Map === 'function'
  );
};

/**
 * Espera a que la API de Google Maps esté completamente cargada
 * @param {number} timeout - Tiempo máximo de espera en milisegundos
 * @returns {Promise} - Una promesa que se resuelve cuando la API está cargada
 */
export const waitForGoogleMapsToLoad = (timeout = 10000) => {
  // Si ya está cargada, devolver una promesa resuelta
  if (isGoogleMapsLoaded()) {
    return Promise.resolve(window.google);
  }

  return new Promise((resolve, reject) => {
    // Tiempo máximo de espera
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      reject(new Error('Tiempo de espera agotado para cargar Google Maps API'));
    }, timeout);

    // Verificar periódicamente si la API está cargada
    const intervalId = setInterval(() => {
      if (isGoogleMapsLoaded()) {
        clearTimeout(timeoutId);
        clearInterval(intervalId);
        resolve(window.google);
      }
    }, 100);
  });
};

export default {
  loadGoogleMapsApi,
  isGoogleMapsLoaded,
  waitForGoogleMapsToLoad
};
